import React, { useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Badge,
  Chip,
  Fade,
  useTheme as useMuiTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CollectionsBookmark as CollectionsBookmarkIcon,
  Terrain as TerrainIcon,
  MenuOpen as MenuOpenIcon,
  AddComment as AddCommentIcon,
  Search as SearchIcon,
  SettingsSuggest as SettingsSuggestIcon,
  SmartToy as SmartToyIcon,
  AccountTree as AccountTreeIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { useSidebar } from '../../context/SidebarContext';
import { useThread } from '../../context/ThreadContext';
import { useRagJob } from '../../context/RagJobContext';
import { Z_INDEX, LAYOUT } from '../../constants/zIndex';
// groupThreadsByDate available but not used — flat "Recents" list preferred (ChatGPT-style)
import ThreadList from '../ThreadList';
import AdminBadge from '../common/AdminBadge';
import UserMenu from './UserMenu';
import ReportFab from '../reports/ReportFab';

import SearchModal from '../search/SearchModal';
import SidebarAgentsList from './SidebarAgentsList';
import SidebarPlansList from './SidebarPlansList';
import { useRoles } from '../../hooks/useRoles';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';

// ─── MUI Mini Drawer pattern ──────────────────────────────────────────────────
// Source: Official MUI docs — https://mui.com/material-ui/react-drawer/
// CRITICAL: Apply openedMixin/closedMixin to BOTH shell and Paper

const openedMixin = (theme) => ({
  width: LAYOUT.SIDEBAR_WIDTH_EXPANDED,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  width: LAYOUT.SIDEBAR_WIDTH_COLLAPSED,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

// CRITICAL: shouldForwardProp prevents 'open' becoming a DOM attribute (React warning)
// CRITICAL: whiteSpace: 'nowrap' prevents text wrapping during width animation
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: LAYOUT.SIDEBAR_WIDTH_EXPANDED,
  flexShrink: 0,
  whiteSpace: 'nowrap',   // prevents text reflow during animation
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

// ─── Constants ────────────────────────────────────────────────────────────────

const TOOLTIP_DELAY = 300;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * UnifiedSidebar — ChatGPT-style collapsible sidebar.
 *
 * Merges NavRail (60px icons) + Sidebar (280px thread panel) into one component.
 * Expanded: 280px with sections — New Chat, Search, Agents (Phase 2), Recents grouped, footer
 * Collapsed: 48px icon-only strip with tooltips
 * Mobile: temporary drawer controlled by isOverlayOpen
 */
const UnifiedSidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { userProfile } = useAuth();
  const { currentOrg } = useOrg();

  const {
    isMobile,
    isTablet,
    isSidebarExpanded,
    toggleSidebar,
    isOverlayOpen,
    closeOverlay,
    closeSidebar,
    isSearchOpen,
    openSearch,
    closeSearch,
  } = useSidebar();

  const { roles } = useRoles();

  const {
    threads,
    currentThread,
    selectThread,
    deleteThread,
    renameThread,
    handleNewThread,
    loadThreads,
    loadMoreThreads,
    hasMore,
    isLoadingMore,
    searchTerm,
    setSearchTerm,
    isSearching,
    agentFilter,
    setAgentFilter,
    setPendingRolId,
  } = useThread();

  const filteredAgent = agentFilter ? roles.find(r => r.id === agentFilter) : null;

  const { hasActiveJob } = useRagJob();

  // Load threads on mount
  useEffect(() => {
    loadThreads(true);
  }, [loadThreads]);

  // ── Navigation items ─────────────────────────────────────────────────────────
  const navItems = [
    {
      id: 'agents',
      label: t('sidebar.agents', 'Gestor de Agentes'),
      icon: <SmartToyIcon />,
      path: '/explore',
      isActive:
        location.pathname === '/explore' ||
        location.pathname.startsWith('/explore/'),
    },
    {
      id: 'plans',
      label: t('sidebar.plans', 'Planes'),
      icon: <AccountTreeIcon />,
      path: '/editor/plans',
      isActive:
        location.pathname === '/editor/plans' ||
        location.pathname.startsWith('/editor/plans'),
    },
    {
      id: 'collections',
      label: t('sidebar.collections', 'Colecciones'),
      icon: (
        <Badge
          variant="dot"
          color="primary"
          invisible={!hasActiveJob}
          sx={{
            '& .MuiBadge-dot': {
              animation: hasActiveJob ? 'rag-pulse 2s ease-in-out infinite' : 'none',
            },
          }}
        >
          <CollectionsBookmarkIcon />
        </Badge>
      ),
      path: '/collections',
      isActive:
        location.pathname === '/collections' ||
        location.pathname.startsWith('/collections/'),
    },
    {
      id: 'presas',
      label: t('sidebar.presas', 'Presas'),
      icon: <TerrainIcon />,
      path: '/presas',
      requiresFeature: 'presas',
      isActive:
        location.pathname === '/presas' ||
        location.pathname.startsWith('/presas/'),
    },
  ];

  // Gating genérico de nav: un item con `requiresFeature` solo se muestra a admins de
  // people1 (role='admin', acceso a todo siempre) o a orgs con ese slug en `features`.
  // Misma regla que FeatureRoute (ruta) y requireFeature (backend).
  const canSeeNavItem = (item) =>
    !item.requiresFeature ||
    userProfile?.role === 'admin' ||
    (Array.isArray(currentOrg?.features) && currentOrg.features.includes(item.requiresFeature));

  // Passive setup nudge for users who skipped onboarding (Notion pattern).
  // Only shown when user explicitly skipped — never shown after full onboarding completion.
  if (userProfile?.onboarding_skipped_at && userProfile?.onboarding_completed === true) {
    navItems.push({
      id: 'complete-setup',
      label: t('sidebar.completeSetup', 'Completar configuración'),
      icon: <SettingsSuggestIcon />,
      path: '/onboarding/chat',
      isActive: location.pathname === '/onboarding/chat',
    });
  }

  const handleNavClick = (item) => {
    if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) {
      if (!isMobile) toggleSidebar();
    } else {
      // Clear agent filter so RECIENTES shows all threads, not a stale per-agent filter
      setAgentFilter(null);
      navigate(item.path);
      if (isSidebarExpanded && !isMobile) closeSidebar();
    }
  };

  const handleThreadSelect = (thread) => {
    selectThread(thread);
    if (location.pathname !== '/') {
      navigate('/');
    }
    if (isMobile) {
      closeOverlay();
    }
  };

  // On mobile overlay the drawer is always fully expanded regardless of localStorage state
  const effectiveExpanded = isMobile || isSidebarExpanded;

  // ── Shared drawer content ────────────────────────────────────────────────────
  const drawerContent = (
    <Box
      component="nav"
      role="navigation"
      aria-label={t('sidebar.mainNavigation', 'Navegación principal')}
      data-sidebar-expanded={effectiveExpanded}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* ── Top actions: collapse toggle + New chat / Search (ChatGPT-style) ── */}
      <List disablePadding sx={{ flexShrink: 0, pt: 0.5, pb: 0.25 }}>
        {/* Collapse toggle — always visible */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip
            title={effectiveExpanded
              ? t('sidebar.collapse', 'Colapsar sidebar')
              : t('sidebar.expand', 'Expandir sidebar')}
            placement="right"
            enterDelay={TOOLTIP_DELAY}
          >
            <ListItemButton
              onClick={isMobile ? closeOverlay : toggleSidebar}
              aria-label={effectiveExpanded
                ? t('sidebar.collapse', 'Colapsar sidebar')
                : t('sidebar.expand', 'Expandir sidebar')}
              sx={{
                minHeight: 32,
                justifyContent: effectiveExpanded ? 'initial' : 'center',
                px: effectiveExpanded ? 1.5 : 1,
                py: 0.25,
                mx: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, mr: effectiveExpanded ? 1.5 : 'auto', justifyContent: 'center', color: 'text.primary' }}
              >
                <MenuOpenIcon fontSize="small" sx={{ transform: effectiveExpanded ? 'none' : 'scaleX(-1)', transition: 'transform 0.2s' }} />
              </ListItemIcon>
              <ListItemText
                primary="AIFlock"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                sx={{ opacity: effectiveExpanded ? 1 : 0 }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* New conversation */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip
            title={effectiveExpanded ? '' : t('sidebar.newChat', 'Nuevo chat')}
            placement="right"
            enterDelay={TOOLTIP_DELAY}
          >
            <ListItemButton
              onClick={() => { setAgentFilter(null); setPendingRolId(null); handleNewThread(); navigate('/'); }}
              sx={{
                minHeight: 32,
                justifyContent: effectiveExpanded ? 'initial' : 'center',
                px: effectiveExpanded ? 1.5 : 1,
                py: 0.25,
                mx: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, mr: effectiveExpanded ? 1.5 : 'auto', justifyContent: 'center', color: 'text.secondary' }}
              >
                <AddCommentIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('sidebar.newChat', 'Nuevo chat')}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                sx={{ opacity: effectiveExpanded ? 1 : 0 }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Search */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <Tooltip
            title={effectiveExpanded ? '' : t('sidebar.search', 'Buscar')}
            placement="right"
            enterDelay={TOOLTIP_DELAY}
          >
            <ListItemButton
              onClick={openSearch}
              sx={{
                minHeight: 32,
                justifyContent: effectiveExpanded ? 'initial' : 'center',
                px: effectiveExpanded ? 1.5 : 1,
                py: 0.25,
                mx: 0.5,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon
                sx={{ minWidth: 0, mr: effectiveExpanded ? 1.5 : 'auto', justifyContent: 'center', color: 'text.secondary' }}
              >
                <SearchIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('sidebar.search', 'Buscar')}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                sx={{ opacity: effectiveExpanded ? 1 : 0 }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      <Divider sx={{ mx: effectiveExpanded ? 1 : 'auto', width: effectiveExpanded ? 'auto' : '60%' }} />

      {/* ── Scrollable area: everything below top actions scrolls together ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* ── Navigation icons (Agents, Plans, Collections) ─────────────── */}
        <List disablePadding sx={{ py: 0.25 }}>
          {navItems.filter(canSeeNavItem).map((item) => (
            <Tooltip
              key={item.id}
              title={effectiveExpanded ? '' : item.label}
              placement="right"
              enterDelay={TOOLTIP_DELAY}
            >
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => handleNavClick(item)}
                  aria-current={item.isActive ? 'page' : undefined}
                  sx={{
                    minHeight: 32,
                    justifyContent: effectiveExpanded ? 'initial' : 'center',
                    px: effectiveExpanded ? 1.5 : 1,
                    py: 0.25,
                    borderRadius: 1,
                    mx: 0.5,
                    bgcolor: item.isActive ? 'action.selected' : 'transparent',
                    color: item.isActive ? 'primary.main' : 'text.secondary',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: effectiveExpanded ? 1.5 : 'auto',
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    sx={{ opacity: effectiveExpanded ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </Tooltip>
          ))}
        </List>

        {/* ── Agents section ──────────────────────────────────────────────── */}
        {effectiveExpanded && (
          <>
            <Divider sx={{ mx: 1, my: 0.75 }} />
            <SidebarAgentsList />
          </>
        )}

        {/* ── Plans section ───────────────────────────────────────────────── */}
        {effectiveExpanded && (
          <>
            <Divider sx={{ mx: 1, my: 0.75 }} />
            <SidebarPlansList />
          </>
        )}

        <Divider sx={{ mx: effectiveExpanded ? 1 : 'auto', width: effectiveExpanded ? 'auto' : '60%', my: 0.75 }} />

        {/* ── Thread list — flat "Recents" (ChatGPT-style) ──────────────── */}
        {effectiveExpanded && (
          <>
            {filteredAgent ? (
              <Fade in timeout={200}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5 }}>
                  <Chip
                    avatar={(() => {
                      const rc = prepareRoleColorForChat(filteredAgent.color || muiTheme.palette.primary.main, muiTheme);
                      return (
                        <Avatar
                          sx={{
                            width: 18,
                            height: 18,
                            fontSize: '0.6rem',
                            background: rc.avatarBgGradient,
                            boxShadow: rc.avatarShadow,
                            color: rc.avatarText,
                          }}
                        >
                          {(filteredAgent.name || '?')[0].toUpperCase()}
                        </Avatar>
                      );
                    })()}
                    label={filteredAgent.name}
                    size="small"
                    onDelete={() => setAgentFilter(null)}
                    sx={{ maxWidth: '100%' }}
                  />
                </Box>
              </Fade>
            ) : (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 0.5,
                  color: 'text.disabled',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.65rem',
                }}
              >
                {t('sidebar.recents', 'Recientes')}
              </Typography>
            )}
            <Fade in key={agentFilter || 'all'} timeout={250}>
              <div>
                <ThreadList
                  threads={threads}
                  currentThreadId={currentThread?.id}
                  onThreadSelect={handleThreadSelect}
                  onThreadDelete={deleteThread}
                  onThreadRename={renameThread}
                  onLoadMore={loadMoreThreads}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  isSearching={isSearching}
                  hideHeader
                  roles={roles}
                />
              </div>
            </Fade>
          </>
        )}
      </Box>

      {/* ── Footer: UserMenu + AdminBtn + ReportFab ──────────────────────── */}
      <Divider sx={{ mx: effectiveExpanded ? 1 : 'auto', width: effectiveExpanded ? 'auto' : '60%' }} />
      <Box sx={{
        py: 0.5,
        display: 'flex',
        flexDirection: effectiveExpanded ? 'row' : 'column',
        justifyContent: effectiveExpanded ? 'space-between' : 'center',
        alignItems: 'center',
        px: effectiveExpanded ? 1 : 0,
        gap: 0.5,
      }}>
        <UserMenu size={36} compact />
        {userProfile?.role === 'admin' && (
          <>
            <Tooltip
              title="Panel de Administración"
              placement={effectiveExpanded ? 'top' : 'right'}
              arrow
            >
              <IconButton
                onClick={() => navigate('/admin')}
                size="small"
                data-testid="admin-panel-button"
                sx={{
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  width: 36,
                  height: 36,
                  // El pulso anima SOLO transform/opacity de un pseudo-elemento: eso
                  // lo compone la GPU sin repintar. Animar box-shadow repintaba cada
                  // fotograma en todas las pantallas y dejaba el proceso de GPU de
                  // Chrome al 100 % mientras hubiera una pestaña abierta.
                  position: 'relative',
                  isolation: 'isolate', // el anillo (z -1) queda tras el icono, sobre el fondo
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    bgcolor: 'rgba(211,47,47,0.45)',
                    zIndex: -1,
                    pointerEvents: 'none',
                    willChange: 'transform, opacity',
                    animation: 'adminPulse 2.5s ease-in-out infinite',
                  },
                  // 50/36 ≈ 1.39: el mismo anillo de 7 px que dibujaba el box-shadow
                  '@keyframes adminPulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.39)', opacity: 0 },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                    '&::after': { animation: 'none', opacity: 0 },
                  },
                  '&:hover': {
                    bgcolor: 'error.dark',
                    transform: 'scale(1.1)',
                    transition: 'transform 0.15s ease',
                  },
                }}
              >
                <AdminPanelSettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <AdminBadge userProfile={userProfile} />
          </>
        )}
        <ReportFab tooltipPlacement={effectiveExpanded ? 'top' : 'right'} />
      </Box>
    </Box>
  );

  // SearchModal — shared between mobile and desktop (MUI Dialog portals to body)
  const searchModal = <SearchModal open={isSearchOpen} onClose={closeSearch} />;

  // ── Mobile: temporary full-screen overlay ─────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Drawer
          variant="temporary"
          anchor="left"
          open={isOverlayOpen}
          onClose={closeOverlay}
          ModalProps={{ keepMounted: true }}
          sx={{
            zIndex: Z_INDEX.SIDEBAR_OVERLAY,
            '& .MuiDrawer-paper': {
              width: LAYOUT.SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
              border: 'none',
              borderRadius: 0,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {searchModal}
      </>
    );
  }

  // ── Desktop + Tablet: permanent styled drawer with width animation ─────────
  return (
    <>
      <StyledDrawer
        variant="permanent"
        open={isSidebarExpanded}
        sx={{
          zIndex: Z_INDEX.NAV_RAIL,
          '& .MuiDrawer-paper': {
            border: 'none',
            borderRight: `1px solid ${muiTheme.palette.divider}`,
            boxSizing: 'border-box',
            borderRadius: 0,
            position: 'fixed',
            top: 'var(--provider-banner-h, 0px)', // #1722: debajo del banner de estado
            left: 0,
            height: 'calc(100dvh - var(--provider-banner-h, 0px))',
          },
        }}
      >
        {drawerContent}
      </StyledDrawer>
      {searchModal}
    </>
  );
};

export default UnifiedSidebar;
